import { createAsyncThunk, createSlice, PayloadAction } from "@reduxjs/toolkit";

import {
  APIResult,
  emptyAPIResult,
  loadingAPIResult,
  successAPIResult,
  errorAPIResult,
  ApiError,
  defaultError,
} from "../../lib/redux";
import {
  staxDatasource,
  triplePSDatasource,
  MarkPaidParams,
} from "../../datasource";
import { OwesReportItem, InvoiceStatus, PaymentType, Order } from "../../models";
import {
  ChargeResponse,
  Customer,
  PaymentMethod,
} from "../../datasource/Stax/models";
import {
  ChargeParams,
  GetCustomersParams,
} from "../../datasource/Stax/StaxInterface";

export interface PaymentsState {
  // API states
  getOrderAPI: APIResult<Order>;
  getCustomersStaxAPI: APIResult<Customer[]>;
  getPaymentMethodsAPI: APIResult<PaymentMethod[]>;
  chargeCardAPI: APIResult<ChargeResponse>;
  markPaidAPI: APIResult<boolean>;
  sendEmailReceiptAPI: APIResult<boolean>;

  // Data
  order?: Order;
  customers: Customer[];
  paymentMethods: PaymentMethod[];
  selectedCustomer?: Customer;
  selectedPaymentMethod?: PaymentMethod;

  // Modal state
  showStaxPaymentModal?: OwesReportItem;
}

const initialState: PaymentsState = {
  getOrderAPI: emptyAPIResult(),
  getCustomersStaxAPI: emptyAPIResult(),
  getPaymentMethodsAPI: emptyAPIResult(),
  chargeCardAPI: emptyAPIResult(),
  markPaidAPI: emptyAPIResult(),
  sendEmailReceiptAPI: emptyAPIResult(),

  customers: [],
  paymentMethods: [],
};

// Async Thunks

export const getOrderForPayment = createAsyncThunk<
  Order,
  string,
  { rejectValue: ApiError }
>("payments/getOrderForPayment", async (orderId, { rejectWithValue }) => {
  try {
    const data = await triplePSDatasource.getOrder(orderId);
    return data;
  } catch (err: any) {
    const error = err.response?.data?.errors?.[0] || defaultError;
    return rejectWithValue(error);
  }
});

export const getCustomersStax = createAsyncThunk<
  Customer[],
  GetCustomersParams,
  { rejectValue: ApiError }
>("payments/getCustomersStax", async (params, { rejectWithValue }) => {
  try {
    const data = await staxDatasource.getCustomers(params);
    return data;
  } catch (err: any) {
    const error = err.response?.data?.errors?.[0] || defaultError;
    return rejectWithValue(error);
  }
});

export const getPaymentMethodsStax = createAsyncThunk<
  PaymentMethod[],
  string,
  { rejectValue: ApiError }
>("payments/getPaymentMethodsStax", async (customerId, { rejectWithValue }) => {
  try {
    const data = await staxDatasource.getPaymentMethods(customerId);
    return data;
  } catch (err: any) {
    const error = err.response?.data?.errors?.[0] || defaultError;
    return rejectWithValue(error);
  }
});

export const chargeCardStax = createAsyncThunk<
  ChargeResponse,
  ChargeParams,
  { rejectValue: ApiError }
>("payments/chargeCardStax", async (params, { rejectWithValue }) => {
  try {
    const data = await staxDatasource.charge(params);
    return data;
  } catch (err: any) {
    const error = err.response?.data?.errors?.[0] || defaultError;
    return rejectWithValue(error);
  }
});

export const markPaidStax = createAsyncThunk<
  boolean,
  MarkPaidParams,
  { rejectValue: ApiError }
>("payments/markPaidStax", async (params, { rejectWithValue }) => {
  try {
    const data = await triplePSDatasource.markPaid(params);
    return data;
  } catch (err: any) {
    const error = err.response?.data?.errors?.[0] || defaultError;
    return rejectWithValue(error);
  }
});

export const sendEmailReceipt = createAsyncThunk<
  boolean,
  string,
  { rejectValue: ApiError }
>("payments/sendEmailReceipt", async (transactionId, { rejectWithValue }) => {
  try {
    const data = await staxDatasource.sendEmailReceipt(transactionId);
    return data;
  } catch (err: any) {
    const error = err.response?.data?.errors?.[0] || defaultError;
    return rejectWithValue(error);
  }
});

// Slice

export const paymentsSlice = createSlice({
  name: "payments",
  initialState,
  reducers: {
    setShowStaxPaymentModal(
      state,
      action: PayloadAction<OwesReportItem | undefined>
    ) {
      state.showStaxPaymentModal = action.payload;
    },
    setSelectedCustomer(state, action: PayloadAction<Customer | undefined>) {
      state.selectedCustomer = action.payload;
      state.paymentMethods = [];
      state.selectedPaymentMethod = undefined;
      state.getPaymentMethodsAPI = emptyAPIResult();
    },
    setSelectedPaymentMethod(
      state,
      action: PayloadAction<PaymentMethod | undefined>
    ) {
      state.selectedPaymentMethod = action.payload;
    },
    resetPaymentsState() {
      return initialState;
    },
    resetChargeCard(state) {
      state.chargeCardAPI = emptyAPIResult();
    },
    resetMarkPaid(state) {
      state.markPaidAPI = emptyAPIResult();
    },
    resetSendEmailReceipt(state) {
      state.sendEmailReceiptAPI = emptyAPIResult();
    },
    resetCustomerSearch(state) {
      state.customers = [];
      state.getCustomersStaxAPI = emptyAPIResult();
      state.selectedCustomer = undefined;
      state.paymentMethods = [];
      state.selectedPaymentMethod = undefined;
      state.getPaymentMethodsAPI = emptyAPIResult();
    },
  },
  extraReducers: (builder) => {
    builder
      // getOrderForPayment
      .addCase(getOrderForPayment.pending, (state) => {
        state.getOrderAPI = loadingAPIResult();
      })
      .addCase(getOrderForPayment.fulfilled, (state, action) => {
        state.getOrderAPI = successAPIResult(action.payload);
        state.order = action.payload;
      })
      .addCase(getOrderForPayment.rejected, (state, action) => {
        state.getOrderAPI = errorAPIResult(action.payload!);
      })
      // getCustomersStax
      .addCase(getCustomersStax.pending, (state) => {
        state.getCustomersStaxAPI = loadingAPIResult();
      })
      .addCase(getCustomersStax.fulfilled, (state, action) => {
        state.getCustomersStaxAPI = successAPIResult(action.payload);
        state.customers = action.payload;
      })
      .addCase(getCustomersStax.rejected, (state, action) => {
        state.getCustomersStaxAPI = errorAPIResult(action.payload!);
      })
      // getPaymentMethodsStax
      .addCase(getPaymentMethodsStax.pending, (state) => {
        state.getPaymentMethodsAPI = loadingAPIResult();
      })
      .addCase(getPaymentMethodsStax.fulfilled, (state, action) => {
        state.getPaymentMethodsAPI = successAPIResult(action.payload);
        state.paymentMethods = action.payload;
      })
      .addCase(getPaymentMethodsStax.rejected, (state, action) => {
        state.getPaymentMethodsAPI = errorAPIResult(action.payload!);
      })
      // chargeCardStax
      .addCase(chargeCardStax.pending, (state) => {
        state.chargeCardAPI = loadingAPIResult();
      })
      .addCase(chargeCardStax.fulfilled, (state, action) => {
        state.chargeCardAPI = successAPIResult(action.payload);
      })
      .addCase(chargeCardStax.rejected, (state, action) => {
        state.chargeCardAPI = errorAPIResult(action.payload!);
      })
      // markPaidStax
      .addCase(markPaidStax.pending, (state) => {
        state.markPaidAPI = loadingAPIResult();
      })
      .addCase(markPaidStax.fulfilled, (state, action) => {
        state.markPaidAPI = successAPIResult(action.payload);
      })
      .addCase(markPaidStax.rejected, (state, action) => {
        state.markPaidAPI = errorAPIResult(action.payload!);
      })
      // sendEmailReceipt
      .addCase(sendEmailReceipt.pending, (state) => {
        state.sendEmailReceiptAPI = loadingAPIResult();
      })
      .addCase(sendEmailReceipt.fulfilled, (state, action) => {
        state.sendEmailReceiptAPI = successAPIResult(action.payload);
      })
      .addCase(sendEmailReceipt.rejected, (state, action) => {
        state.sendEmailReceiptAPI = errorAPIResult(action.payload!);
      });
  },
});

export const {
  setShowStaxPaymentModal,
  setSelectedCustomer,
  setSelectedPaymentMethod,
  resetPaymentsState,
  resetChargeCard,
  resetMarkPaid,
  resetSendEmailReceipt,
  resetCustomerSearch,
} = paymentsSlice.actions;

export default paymentsSlice.reducer;
